import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
    Ghost, HeartCrack, Target, TrendingUp,
    Pizza, Utensils, Home as HomeIcon, Leaf,
    BatteryLow, BatteryMedium, BatteryFull, ShieldCheck,
    Ban, Activity, Dumbbell, Flame, ArrowLeft,
} from "lucide-react";
import { StepShell } from "@/components/revanche/StepShell";
import { PrimaryCTA } from "@/components/revanche/PrimaryCTA";
import { getConsent, setConsent } from "@/lib/analytics";
import { AnswerCard } from "@/components/revanche/AnswerCard";
import { WheelPicker } from "@/components/revanche/WheelPicker";
import { ProgressBar } from "@/components/revanche/ProgressBar";
import { MotivationSlider } from "@/components/revanche/MotivationSlider";
import { AccountStep } from "@/components/revanche/AccountStep";
import { saveAnswers, completeQuestionnaire, fetchSession } from "@/lib/api";
import { EASE, haptic } from "@/lib/design";

const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
const BODY_OPTIONS = [8, 10, 12, 15, 18, 20, 25, 30];

const STEPS = [
    {
        type: "text", key: "prenom",
        title: "C'est quoi ton prénom ?",
        explanation: "Pour personnaliser ton analyse et ton coaching.",
    },
    {
        type: "wheel", key: "age",
        title: "Tu as quel âge ?",
        explanation: "Pour calibrer ton analyse à ton âge.",
        options: range(13, 70), unit: "ans", def: 25,
    },
    { type: "mirror", title: "Personne n'arrive à se juger objectivement. Nous, si." },
    {
        type: "qcm", key: "situation",
        title: "Ta situation en ce moment ?",
        explanation: "Pour comprendre d'où tu pars.",
        options: [
            "Je me fais ghoster ou recaler",
            "Je sors d'une rupture",
            "Je veux plaire plus",
            "Je veux devenir la meilleure version de moi",
        ],
        icons: [Ghost, HeartCrack, Target, TrendingUp],
    },
    {
        type: "qcm", key: "confiance",
        title: "Ta confiance en toi en ce moment ?",
        explanation: "Ta confiance change la façon dont les autres te perçoivent.",
        options: ["Au plus bas", "Moyenne", "Bonne mais irrégulière", "Solide"],
        icons: [BatteryLow, BatteryMedium, BatteryFull, ShieldCheck],
    },
    {
        type: "account",
        title: "On sauvegarde ta progression.",
        explanation: "Pour que tu retrouves ton analyse à tout moment.",
    },
    {
        type: "qcm", key: "alimentation",
        title: "Ton alimentation, c'est plutôt…",
        explanation: "Ton alimentation se voit sur ta peau et ton visage.",
        options: [
            "Fast-food et sucré souvent",
            "Un peu de tout",
            "Surtout fait maison",
            "Je fais attention à tout",
        ],
        icons: [Pizza, Utensils, HomeIcon, Leaf],
    },
    {
        type: "height_weight",
        title: "Ta taille et ton poids",
        explanation: "Pour calculer ton IMC et ton potentiel physique.",
    },
    {
        type: "body", key: "masse_grasse",
        title: "Choisis le corps le plus proche du tien",
        explanation: "Pour situer ta composition corporelle actuelle.",
    },
    {
        type: "qcm", key: "sport",
        title: "Tu fais du sport combien de fois par semaine ?",
        explanation: "Le sport façonne ta posture et ta mâchoire.",
        options: ["Jamais", "1 à 2 fois", "3 à 4 fois", "5 fois ou plus"],
        icons: [Ban, Activity, Dumbbell, Flame],
    },
    {
        type: "motivation", key: "motivation",
        title: "Tu es motivé à combien, là, tout de suite ?",
        explanation: "Sois honnête, ça sert à adapter ton plan.",
        def: 50,
    },
];

const TOTAL = STEPS.length;

// ---------- Écran miroir ----------
const MirrorScreen = ({ title, step, onBack, onContinue, reduce }) => (
    <div className="absolute inset-0 flex flex-col bg-black">
        <div className="relative" style={{ height: "50%" }}>
            <img src="/pitch_mirror.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.25), rgba(0,0,0,0.1) 45%, #000000)" }} />
            <div className="absolute inset-x-0 top-0 safe-top" style={{ paddingLeft: 24, paddingRight: 24, paddingTop: 12 }}>
                <button type="button" onClick={onBack} data-testid="btn-back" aria-label="Retour"
                    className="tap-feedback w-9 h-9 -ml-1.5 flex items-center justify-center rounded-full active:scale-[0.98] mb-3">
                    <ArrowLeft size={22} strokeWidth={1.5} color="#FFFFFF" />
                </button>
                <ProgressBar value={step + 1} max={TOTAL} />
            </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col" style={{ paddingLeft: 24, paddingRight: 24 }}>
            <motion.h1
                data-testid="question-title"
                className="font-body"
                style={{ fontSize: "28px", fontWeight: 700, lineHeight: 1.15, color: "var(--text)", marginTop: 24 }}
                initial={{ opacity: 0, y: reduce ? 0 : 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: reduce ? 0.12 : 0.42, ease: EASE }}
            >
                {title}
            </motion.h1>
        </div>
        <div className="shrink-0 safe-bottom" style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 24, paddingTop: 8 }}>
            <PrimaryCTA dataTestId="btn-continue" onClick={onContinue}>Continuer</PrimaryCTA>
        </div>
    </div>
);

// ---------- Grille silhouettes ----------
const BodyGrid = ({ value, onSelect }) => (
    <div className="grid grid-cols-2 gap-3 pb-2">
        {BODY_OPTIONS.map((bf) => {
            const selected = value === bf;
            return (
                <button
                    key={bf}
                    type="button"
                    onClick={() => onSelect(bf)}
                    aria-pressed={selected}
                    data-testid={`body-option-${bf}`}
                    className="tap-feedback rounded-2xl overflow-hidden flex flex-col items-center justify-end active:scale-[0.98]"
                    style={{
                        backgroundColor: selected ? "var(--surface-2)" : "var(--surface)",
                        border: selected ? "1.5px solid var(--red)" : "1px solid var(--border)",
                        transition: "background-color var(--dur-touch) var(--ease), border-color var(--dur-touch) var(--ease), transform var(--dur-touch) var(--ease)",
                    }}
                >
                    <img
                        src={`/silhouettes/bf_${String(bf).padStart(2, "0")}.webp`}
                        alt={`${bf} % de masse grasse`}
                        className="w-full h-28 object-contain"
                        loading="lazy"
                    />
                    <span
                        className="w-full text-center font-body font-medium"
                        style={{ fontSize: "16px", color: selected ? "#FFFFFF" : "var(--text-secondary)", paddingBottom: 8 }}
                    >
                        {bf} %
                    </span>
                </button>
            );
        })}
    </div>
);

const Questionnaire = () => {
    const navigate = useNavigate();
    const reduce = useReducedMotion();
    const [step, setStep] = useState(0);
    const [direction, setDirection] = useState(1);
    const [answers, setAnswers] = useState({});
    const [ready, setReady] = useState(false);
    const [consent, setConsentChoice] = useState(getConsent());
    const stepRef = useRef(0);
    const chooseConsent = (c) => { setConsent(c); setConsentChoice(c); };

    useEffect(() => { stepRef.current = step; }, [step]);

    useEffect(() => {
        let active = true;
        const localA = JSON.parse(localStorage.getItem("revanche_answers") || "{}");
        const localStep = parseInt(localStorage.getItem("revanche_step") || "0", 10);
        const params = new URLSearchParams(window.location.search);
        const dev = params.get("dev_step");
        let initialStep = 0;
        if (dev != null) {
            const seed = { prenom: "Lucas", age: 25, situation: "Je veux plaire plus", confiance: "Moyenne", alimentation: "Un peu de tout", taille: 180, poids: 78, masse_grasse: 15, sport: "3 à 4 fois", motivation: 70 };
            setAnswers({ ...seed, ...localA });
            initialStep = Math.min(Math.max(parseInt(dev, 10) || 0, 0), STEPS.length - 1);
        } else {
            setAnswers(localA);
            initialStep = Number.isFinite(localStep) ? Math.min(Math.max(localStep, 0), STEPS.length - 1) : 0;
        }
        setStep(initialStep);
        stepRef.current = initialStep;
        try {
            window.history.replaceState({ rvStep: 0 }, "");
            for (let i = 1; i <= initialStep; i += 1) {
                window.history.pushState({ rvStep: i }, "");
            }
        } catch (e) { /* noop */ }
        setReady(true);
        fetchSession().then((s) => {
            if (active && s && s.answers && Object.keys(s.answers).length && dev == null) {
                setAnswers((prev) => ({ ...s.answers, ...prev }));
            }
        });
        return () => { active = false; };
    }, []);

    useEffect(() => { if (ready) localStorage.setItem("revanche_step", String(step)); }, [step, ready]);
    useEffect(() => { if (ready) localStorage.setItem("revanche_answers", JSON.stringify(answers)); }, [answers, ready]);

    const handlePop = useCallback((e) => {
        const rv = e && e.state && typeof e.state.rvStep === "number" ? e.state.rvStep : null;
        if (rv == null) { navigate("/"); return; }
        setDirection(rv < stepRef.current ? -1 : 1);
        setStep(rv);
    }, [navigate]);

    useEffect(() => {
        window.addEventListener("popstate", handlePop);
        return () => window.removeEventListener("popstate", handlePop);
    }, [handlePop]);

    const persist = useCallback((a, s) => {
        localStorage.setItem("revanche_answers", JSON.stringify(a));
        localStorage.setItem("revanche_step", String(s));
        saveAnswers(a, s).catch(() => {});
    }, []);

    const current = STEPS[step];
    const setAnswer = (key, val) => setAnswers((prev) => ({ ...prev, [key]: val }));

    const goNext = (override) => {
        const merged = override || answers;
        const nextStep = step + 1;
        if (nextStep >= STEPS.length) {
            const final = { ...merged, effort: `Motivé à ${merged.motivation ?? 50} %` };
            localStorage.setItem("revanche_answers", JSON.stringify(final));
            completeQuestionnaire(final).catch(() => {});
            navigate("/photo");
            return;
        }
        persist(merged, nextStep);
        window.history.pushState({ rvStep: nextStep }, "");
        setDirection(1);
        setStep(nextStep);
    };

    const goBack = () => {
        if (step <= 0) navigate("/");
        else window.history.back();
    };

    const selectAndAdvance = (key, val) => {
        const next = { ...answers, [key]: val };
        setAnswers(next);
        haptic();
        setTimeout(() => goNext(next), 250);
    };

    // Étape compte : e-mail ou code testeur (l'appel serveur est fait dans AccountStep)
    const accountDone = () => goNext();

    // Saute l'étape compte pendant un rescan (utilisateur déjà connecté)
    useEffect(() => {
        if (!ready || !current) return;
        if (current.type === "account" && localStorage.getItem("revanche_rescan") === "1") {
            goNext();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, ready]);

    const isValid = () => {
        if (!current) return false;
        switch (current.type) {
            case "text": return (answers.prenom || "").trim().length > 0;
            case "height_weight": return answers.taille != null && answers.poids != null;
            case "wheel": return answers[current.key] != null;
            default: return true;
        }
    };

    useEffect(() => {
        if (!current) return;
        if (current.type === "wheel" && answers[current.key] == null) setAnswer(current.key, current.def);
        if (current.type === "motivation" && answers.motivation == null) setAnswer("motivation", current.def);
        if (current.type === "height_weight") {
            if (answers.taille == null) setAnswer("taille", 175);
            if (answers.poids == null) setAnswer("poids", 70);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    if (!ready) return <div className="fixed inset-0 bg-black" />;

    const variants = {
        enter: (dir) => ({ x: reduce ? 0 : (dir > 0 ? 24 : -24), opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (dir) => ({ x: reduce ? 0 : (dir > 0 ? -24 : 24), opacity: 0 }),
    };

    const renderQcmIcon = (cfg, i) => (selected) => {
        const I = cfg.icons[i];
        return <I size={20} strokeWidth={1.5} color={selected ? "#FFFFFF" : "var(--text-secondary)"} />;
    };

    const renderStep = () => {
        if (current.type === "mirror") {
            return <MirrorScreen title={current.title} step={step} onBack={goBack} onContinue={() => goNext()} reduce={reduce} />;
        }

        const shell = {
            questionNumber: step + 1,
            totalQuestions: TOTAL,
            onBack: goBack,
            title: current.title,
            explanation: current.explanation,
        };

        if (current.type === "text") {
            return (
                <StepShell {...shell} showCta onContinue={() => goNext()} continueDisabled={!isValid() || !consent}>
                    <input
                        data-testid="input-prenom"
                        type="text"
                        autoFocus
                        inputMode="text"
                        autoComplete="given-name"
                        placeholder="Ton prénom"
                        value={answers.prenom || ""}
                        onChange={(e) => setAnswer("prenom", e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && isValid() && consent) goNext(); }}
                        className="w-full font-body outline-none"
                        style={{ height: "var(--h-field)", padding: "0 16px", borderRadius: "var(--radius-field)", backgroundColor: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)", fontSize: "16px" }}
                    />
                    {!consent && (
                        <div className="mt-4" data-testid="consent-block">
                            <p className="font-body" style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.4 }}>
                                Cookies de mesure d'audience (Google Analytics et Microsoft Clarity){" "}
                                <a href="/confidentialite" data-testid="consent-learn-more" className="underline" style={{ color: "var(--text-secondary)" }}>En savoir plus</a>
                            </p>
                            <div className="flex gap-2 mt-2">
                                <button data-testid="consent-accept" onClick={() => chooseConsent("granted")}
                                    className="tap-feedback flex-1 rounded-full font-body" style={{ height: 40, fontSize: 13, border: "1px solid var(--border)", color: "var(--text)" }}>
                                    Accepter
                                </button>
                                <button data-testid="consent-refuse" onClick={() => chooseConsent("denied")}
                                    className="tap-feedback flex-1 rounded-full font-body" style={{ height: 40, fontSize: 13, border: "1px solid var(--border)", color: "var(--text)" }}>
                                    Refuser
                                </button>
                            </div>
                        </div>
                    )}
                </StepShell>
            );
        }

        if (current.type === "account") {
            return (
                <StepShell {...shell}>
                    <AccountStep onDone={accountDone} />
                </StepShell>
            );
        }

        if (current.type === "wheel") {
            return (
                <StepShell {...shell} showCta onContinue={() => goNext()} continueDisabled={!isValid()}>
                    <div className="flex-1 flex items-center">
                        <WheelPicker dataTestId="wheel-age" options={current.options} value={answers[current.key] ?? current.def} unit={current.unit} onChange={(v) => setAnswer(current.key, v)} />
                    </div>
                </StepShell>
            );
        }

        if (current.type === "motivation") {
            return (
                <StepShell {...shell} showCta onContinue={() => goNext()}>
                    <MotivationSlider value={answers.motivation ?? current.def} onChange={(v) => setAnswer("motivation", v)} />
                </StepShell>
            );
        }

        if (current.type === "height_weight") {
            return (
                <StepShell {...shell} showCta onContinue={() => goNext()} continueDisabled={!isValid()}>
                    <div className="flex-1 flex items-center gap-4">
                        <div className="flex-1">
                            <p className="font-body text-center mb-2" style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Taille</p>
                            <WheelPicker dataTestId="wheel-taille" options={range(140, 210)} value={answers.taille ?? 175} unit="cm" onChange={(v) => setAnswer("taille", v)} />
                        </div>
                        <div className="flex-1">
                            <p className="font-body text-center mb-2" style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Poids</p>
                            <WheelPicker dataTestId="wheel-poids" options={range(40, 150)} value={answers.poids ?? 70} unit="kg" onChange={(v) => setAnswer("poids", v)} />
                        </div>
                    </div>
                </StepShell>
            );
        }

        if (current.type === "body") {
            return (
                <StepShell {...shell} scrollable>
                    <BodyGrid value={answers.masse_grasse} onSelect={(v) => selectAndAdvance("masse_grasse", v)} />
                </StepShell>
            );
        }

        // QCM à passage automatique
        return (
            <StepShell {...shell}>
                <div className="flex flex-col gap-3">
                    {current.options.map((opt, i) => (
                        <AnswerCard
                            key={opt}
                            dataTestId={`qcm-option-${i}`}
                            label={opt}
                            renderIcon={renderQcmIcon(current, i)}
                            selected={answers[current.key] === opt}
                            onClick={() => selectAndAdvance(current.key, opt)}
                        />
                    ))}
                </div>
            </StepShell>
        );
    };

    return (
        <div className="fixed inset-0 bg-black overflow-hidden max-w-[480px] mx-auto">
            <AnimatePresence initial={false} custom={direction} mode="popLayout">
                <motion.div
                    key={step}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: reduce ? 0.12 : 0.42, ease: EASE }}
                    className="absolute inset-0"
                >
                    {renderStep()}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default Questionnaire;
